import React from 'react';
import { StyleSheet } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import DiscountIcon from '@/assets/icons/discountFrame';
import InflatedIcon from '@/assets/icons/inflatedFrame';
import LiveIcon from '@/assets/icons/live-indicator';
import LockIcon from '@/assets/icons/lock';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { designSystem, withOpacity } from '@/styles/styles';
import { EventStatus, MarketOption, MarketStatus } from '@/types/api.generated';
import { getColorByProjType } from '@/utils/getProjectionsTypes';
import { logger } from '@/utils/logging';

import { BetslipPick } from '../betslip-pickem/types';
import { PickButtons } from './PickButtons';

const color = designSystem.colors;

export const DiscountedProjectionCard = ({
    projection,
    event,
    playerPicked,
    removePlayer,
    selectMarketOption,
}: {
    projection: PlayerProjectionFragment;
    event: EventInfo;
    playerPicked?: BetslipPick;
    removePlayer: () => void;
    selectMarketOption: (projection: PlayerProjectionFragment, option: MarketOption) => void;
}) => {
    const specialIncrease = (projection?.nonRegularPercentage && projection?.nonRegularPercentage > 0) || false;
    const projectionColor = getColorByProjType(projection.type, specialIncrease);
    const projectionMappedColor = color[projectionColor];
    const isLive = event?.status === EventStatus.InProgress;
    const isSuspended = projection.marketStatus === MarketStatus.Suspended;
    return (
        <Box
            paddingHorizontal={'s16'}
            marginBottom={'s16'}
            paddingVertical={'s12'}
            style={[
                styles.specialProjectionContainer,
                {
                    backgroundColor: withOpacity(projectionMappedColor, '0.02'),
                    borderColor: withOpacity(projectionMappedColor, '0.04'),
                },
            ]}
            key={projection.marketId}
        >
            <Row justifyContent={'space-between'} alignItems={'center'}>
                <Row alignItems={'center'}>
                    {specialIncrease ? <InflatedIcon /> : <DiscountIcon color={designSystem.colors.special} />}
                    <Box marginLeft={'s16'}>
                        <Row alignItems={'center'}>
                            <Text
                                style={{ color: withOpacity(projectionMappedColor, '0.3') }}
                                variant={'bodyLarge'}
                                textDecorationLine={'line-through'}
                            >
                                {projection.value}
                            </Text>
                            <SizedBox value={4} />
                            <AnimatedNumber
                                decimals={1}
                                value={projection.nonRegularValue ?? 0}
                                color={projectionColor}
                                variant={'titleLarge'}
                                alignText={'flex-start'}
                                lineHeight={20}
                            />
                            {isLive && projection.currentValue ? (
                                <>
                                    <Box mr={'s6'} ml={'s6'}>
                                        <LiveIcon size={10} />
                                    </Box>
                                    <Text color={'red500'} variant={'labelMedium'}>
                                        {projection.currentValue}
                                    </Text>
                                </>
                            ) : null}
                        </Row>
                        <Text variant={'bodyMedium'} color={projectionColor} opacity={0.8}>
                            {projection.label}
                        </Text>
                    </Box>
                </Row>
                <Box ml={'s16'} justifyContent={'center'}>
                    {isSuspended ? (
                        <Row justifyContent={'center'} alignItems={'center'}>
                            <Text color={'gray4'}>Suspended</Text>
                            <Box p={'s12'}>
                                <LockIcon color={designSystem.colors.gray4} />
                            </Box>
                        </Row>
                    ) : (
                        <PickButtons
                            removeSelection={removePlayer}
                            makeSelection={outcome => {
                                const marketOption = projection.allowedOptions?.find(it => it.outcome === outcome);
                                if (!marketOption) {
                                    logger.warn('No market option found for outcome', outcome);
                                    return;
                                }
                                const marketId = projection.marketId;
                                if (!marketId) {
                                    logger.warn('No market id found for projection', projection.label);
                                    return;
                                }
                                selectMarketOption(projection, marketOption);
                            }}
                            projection={projection}
                            playerPicked={playerPicked}
                        />
                    )}
                </Box>
            </Row>
        </Box>
    );
};

const styles = StyleSheet.create({
    specialProjectionContainer: {
        borderWidth: 1,
        borderRadius: 16,
    },
    squareButton: {
        width: 60,
        height: 36,
    },
});
